import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  School as SchoolIcon,
  LocalHospital as HealthIcon,
  DirectionsBus as TransitIcon,
  ShoppingCart as ShoppingIcon,
  Park as ParkIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { NeighborhoodResult } from '../../../types/property';

interface NeighborhoodTabProps {
  data: NeighborhoodResult;
  loading?: boolean;
  error?: string;
}

const NeighborhoodTab: React.FC<NeighborhoodTabProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading neighborhood analysis...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No neighborhood analysis available
      </Alert>
    );
  }

  const { neighborhoodIntelligence } = data;
  const { demographics, amenities, marketContext, neighborhoodScore, insights, recommendations } = neighborhoodIntelligence;

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Neighborhood Score Overview */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon color="primary" />
                Neighborhood Overview
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: '1 1 150px', textAlign: 'center', p: 2 }}>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {Math.round(neighborhoodScore.overall * 100)}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Overall Score
                  </Typography>
                </Box>
                
                {Object.entries(neighborhoodScore).filter(([key]) => key !== 'overall').map(([key, score]) => (
                  <Box sx={{ flex: '1 1 150px', textAlign: 'center', p: 1 }} key={key}>
                    <Typography variant="h6" color={getScoreColor(score)}>
                      {Math.round(score * 100)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {key}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Demographics */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Demographics
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="body2" color="text.secondary">Population</Typography>
                    <Typography variant="h6">{demographics.population.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="body2" color="text.secondary">Median Age</Typography>
                    <Typography variant="h6">{demographics.medianAge} years</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="body2" color="text.secondary">Median Income</Typography>
                    <Typography variant="h6">${demographics.medianIncome.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography variant="body2" color="text.secondary">Homeownership Rate</Typography>
                    <Typography variant="h6">{demographics.homeownershipRate}%</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Market Context */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" />
                  Market Context
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Price Trends</Typography>
                  <Typography variant="body1">1Y: +{marketContext.priceTrends.oneYear}%</Typography>
                  <Typography variant="body1">5Y: +{marketContext.priceTrends.fiveYear}%</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Investment Potential</Typography>
                  <Typography variant="body1">Rental Yield: {marketContext.investmentPotential.rentalYield}%</Typography>
                  <Typography variant="body1">Appreciation: {marketContext.investmentPotential.appreciation}%</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Amenities */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Local Amenities
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SchoolIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">Education</Typography>
                  </Box>
                  <Typography variant="body2">Schools Rating: {amenities.schools.rating}/10</Typography>
                  <Typography variant="body2">Elementary: {amenities.schools.elementary}</Typography>
                  <Typography variant="body2">Middle: {amenities.schools.middle}</Typography>
                  <Typography variant="body2">High: {amenities.schools.high}</Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TransitIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">Transportation</Typography>
                  </Box>
                  <Typography variant="body2">Walkability: {amenities.transportation.walkability}/100</Typography>
                  <Typography variant="body2">Public Transit: {amenities.transportation.publicTransit}/100</Typography>
                  <Typography variant="body2">Bike Lanes: {amenities.transportation.bikeLanes}/100</Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SecurityIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">Safety</Typography>
                  </Box>
                  <Typography variant="body2">Safety Score: {amenities.safety.safetyScore}/100</Typography>
                  <Typography variant="body2">Police Stations: {amenities.safety.policeStations}</Typography>
                  <Typography variant="body2">Fire Stations: {amenities.safety.fireStations}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Insights */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon color="success" />
                  Strengths & Opportunities
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
                    Strengths
                  </Typography>
                  <List dense>
                    {insights.strengths.map((strength, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={strength} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                {insights.opportunities.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'info.main' }}>
                      Opportunities
                    </Typography>
                    <List dense>
                      {insights.opportunities.map((opportunity, index) => (
                        <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <TrendingUpIcon fontSize="small" color="info" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={opportunity} 
                            slotProps={{ primary: { variant: 'body2' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Recommendations */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  Considerations & Recommendations
                </Typography>

                {insights.weaknesses.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'warning.main' }}>
                      Areas of Concern
                    </Typography>
                    <List dense>
                      {insights.weaknesses.map((weakness, index) => (
                        <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <WarningIcon fontSize="small" color="warning" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={weakness} 
                            slotProps={{ primary: { variant: 'body2' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {recommendations.forBuyers && recommendations.forBuyers.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
                      For Buyers
                    </Typography>
                    <List dense>
                      {recommendations.forBuyers.map((rec, index) => (
                        <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={rec} 
                            slotProps={{ primary: { variant: 'body2' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NeighborhoodTab;